import { createApi } from "@reduxjs/toolkit/query/react";

import { type ISignInData, type ISignUpData, type ITokens } from "@/providers/store/api/auth/auth.interface";
import { setToken } from "@/providers/store/api/auth/functions";
import { safeInvalidateTags } from "@/providers/store/api/safe-invalidate-tags";
import usersApi from "@/providers/store/api/users/users";
import { logout } from "@/providers/store/slices/user/slice";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Auth, ApiTags.Users],
  endpoints: (builder) => ({
    signIn: builder.mutation<ITokens, ISignInData>({
      query: ({ email, password }) => ({
        url: "/auth/login",
        method: "POST",
        body: { email, password },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.Auth, ApiTags.Users]),
      transformResponse: (result: IApiResponse<ITokens>) => {
        const { data } = result;

        return setToken(data, true);
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.resetApiState());
        await dispatch(usersApi.endpoints.getUser.initiate(null));
      },

    }),
    signUp: builder.mutation<ITokens, ISignUpData>({
      query: ({
        password, firstName, lastName, token,
      }) => ({
        url: "/auth/register",
        method: "POST",
        body: {
          password,
          firstName,
          lastName,
          token,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.Auth, ApiTags.Users]),
      transformResponse: (result: IApiResponse<ITokens>) => {
        const { data } = result;

        return setToken(data, true);
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.resetApiState());
        await dispatch(usersApi.endpoints.getUser.initiate(null));
      },
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
      invalidatesTags: [ApiTags.Auth, ApiTags.Users],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(logout());
        dispatch(usersApi.util.resetApiState());
      },
    }),
    requestPasswordReset: builder.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: "/auth/password/request-reset",
        method: "POST",
        body: {
          email,
        },
      }),
    }),
    confirmPasswordReset: builder.mutation<void, { token: string; newPassword: string }>({
      query: ({ token, newPassword }) => ({
        url: "/auth/password/reset",
        method: "POST",
        body: {
          token,
          newPassword,
        },
      }),
    }),
    googleLink: builder.mutation<{ url: string }, void>({
      query: () => ({
        url: "/auth/google/link",
        method: "POST",
        body: {},
      }),
      transformResponse: (result: IApiResponse<{ url: string }>) => {
        const { data } = result;

        return data;
      },
    }),
    googleCallback: builder.mutation<ITokens, { code: string }>({
      query: ({ code }) => ({
        url: "/auth/google/callback",
        method: "POST",
        body: {
          code,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.Auth, ApiTags.Users]),
      transformResponse: (result: IApiResponse<ITokens>) => {
        const { data } = result;

        return setToken(data, true);
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.resetApiState());
        await dispatch(usersApi.endpoints.getUser.initiate(null));
      },
    }),
    googleSignUpLink: builder.mutation<{ url: string }, { state: string }>({
      query: ({ state }) => ({
        url: "/auth/register/google/link",
        method: "POST",
        body: {
          state,
        },
      }),
      transformResponse: (result: IApiResponse<{ url: string }>) => {
        const { data } = result;

        return data;
      },
    }),
    googleSignUpCallback: builder.mutation<ITokens, { code: string; state: string }>({
      query: ({ code, state }) => ({
        url: "/auth/register/google/callback",
        method: "POST",
        body: {
          code,
          state,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.Auth, ApiTags.Users]),
      transformResponse: (result: IApiResponse<ITokens>) => {
        const { data } = result;

        return setToken(data, true);
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.resetApiState());
        await dispatch(usersApi.endpoints.getUser.initiate(null));
      },
    }),
    microsoftLink: builder.mutation<{ url: string }, void>({
      query: () => ({
        url: "/auth/microsoft/link",
        method: "POST",
        body: {},
      }),
      transformResponse: (result: IApiResponse<{ url: string }>) => {
        const { data } = result;

        return data;
      },
    }),
    microsoftCallback: builder.mutation<ITokens, { code: string }>({
      query: ({ code }) => ({
        url: "/auth/microsoft/callback",
        method: "POST",
        body: {
          code,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.Auth, ApiTags.Users]),
      transformResponse: (result: IApiResponse<ITokens>) => {
        const { data } = result;

        return setToken(data, true);
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.resetApiState());
        await dispatch(usersApi.endpoints.getUser.initiate(null));
      },
    }),
    microsoftSignUpLink: builder.mutation<{ url: string }, { state: string }>({
      query: ({ state }) => ({
        url: "/auth/register/microsoft/link",
        method: "POST",
        body: {
          state,
        },
      }),
      transformResponse: (result: IApiResponse<{ url: string }>) => {
        const { data } = result;

        return data;
      },
    }),
    microsoftSignUpCallback: builder.mutation<ITokens, { code: string; state: string }>({
      query: ({ code, state }) => ({
        url: "/auth/register/microsoft/callback",
        method: "POST",
        body: {
          code,
          state,
        },
      }),
      invalidatesTags: safeInvalidateTags([ApiTags.Auth, ApiTags.Users]),
      transformResponse: (result: IApiResponse<ITokens>) => {
        const { data } = result;

        return setToken(data, true);
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.resetApiState());
        await dispatch(usersApi.endpoints.getUser.initiate(null));
      },
    }),
  }),
});

export const {
  useLogoutMutation,
  useSignInMutation,
  useSignUpMutation,
  useRequestPasswordResetMutation,
  useConfirmPasswordResetMutation,
  useGoogleCallbackMutation,
  useGoogleLinkMutation,
  useMicrosoftCallbackMutation,
  useMicrosoftLinkMutation,
  useGoogleSignUpLinkMutation,
  useGoogleSignUpCallbackMutation,
  useMicrosoftSignUpLinkMutation,
  useMicrosoftSignUpCallbackMutation,
} = authApi;

export default authApi;
